import { codeMap, http } from './http';

/**
 * 정기보고 — 회차 · 요청 · 받은 자료 · 코멘트 · 메일 이력.
 *
 * 읽기와 단순 수정(코멘트 · 받은 자료 체크)은 일반 REST, **상태가 바뀌는 것은 전용 엔드포인트**
 * (`/report-rounds/create` · `/report-requests/:id/send|submit|confirm|reject` · `/report-rounds/:id/close`).
 * 서버가 전이를 검사하고 알림 · 변경 이력을 같이 남긴다.
 */
export const REQ_STATUS = codeMap({ not_sent: '미요청', requested: '요청', submitted: '제출', confirmed: '확정', rejected: '반려' } as const);
export const ROUND_STATUS = codeMap({ open: '진행 중', closed: '종료' } as const);
export const TERM = codeMap({ H1: '상반기', H2: '하반기', Q1: '1분기', Q2: '2분기', Q3: '3분기', Q4: '4분기', FY: '연간' } as const);
export const ITEM_STATUS = codeMap({ received: '받음', missing: '없음', not_applicable: '해당 없음' } as const);
export const SIDE = codeMap({ us: '우리', company: '투자처' } as const);
export const MAIL_KIND = codeMap({ request: '요청', remind: '재요청', reject: '반려', confirm: '확정' } as const);

export type ReqStatus = '미요청' | '요청' | '제출' | '확정' | '반려';
export type ItemStatus = '받음' | '없음' | '해당 없음';
export const REQ_STATUSES: ReqStatus[] = ['미요청', '요청', '제출', '확정', '반려'];
export const TERMS = ['상반기', '하반기', '1분기', '2분기', '3분기', '4분기', '연간'] as const;

export interface ReportItem { id: number; name: string; sortOrder: number; isActive: boolean }
export interface Round {
  id: number; year: number; term: string; title: string; startOn: string; dueOn: string | null;
  status: '진행 중' | '종료'; includeRisk: boolean; closedAt: string | null; itemIds: number[];
}
export interface Request {
  id: number; roundId: number; companyId: number; status: ReqStatus;
  sentAt: string | null; submittedAt: string | null; confirmedAt: string | null; rejectReason: string | null; replyDueOn: string | null;
}
export interface Comment { id: number; requestId: number; side: '우리' | '투자처'; authorName: string; body: string; createdAt: string }
export interface MailLog { id: number; requestId: number; kind: string; toEmail: string; subject: string; status: 'sent' | 'failed' | 'bounced'; error: string | null; sentAt: string }
export interface Detail { items: Record<number, ItemStatus>; comments: Comment[]; mails: MailLog[] }
export interface Board { rounds: Round[]; requests: Request[]; items: ReportItem[] }

const ACTOR = '김성배'; // 로그인 전 — 서버 기본값과 같다
const toReq = (r: any): Request => ({ ...r, status: REQ_STATUS.toUi(r.status)! });
/** 상태가 바뀌면 알림 종을 다시 읽게 한다 */

export const reportApi = {
  async board(): Promise<Board> {
    const [rounds, links, requests, items] = await Promise.all([
      http.get<any[]>('/report-rounds'), http.get<{ roundId: number; itemId: number }[]>('/report-round-items'),
      http.get<any[]>('/report-requests'), http.get<ReportItem[]>('/report-items'),
    ]);
    return {
      rounds: rounds
        .map((r) => ({ ...r, status: ROUND_STATUS.toUi(r.status)!, itemIds: links.filter((l) => l.roundId === r.id).map((l) => l.itemId) }))
        .sort((a, b) => b.startOn.localeCompare(a.startOn) || b.id - a.id),
      requests: requests.map(toReq),
      items,
    };
  },
  /** 요청별 코멘트 수 — 표의 「코멘트」 열 */
  async commentCounts(): Promise<Record<number, number>> {
    const out: Record<number, number> = {};
    (await http.get<{ requestId: number }[]>('/report-comments')).forEach((c) => { out[c.requestId] = (out[c.requestId] ?? 0) + 1; });
    return out;
  },
  async detail(requestId: number): Promise<Detail> {
    const [ri, cs, ms] = await Promise.all([
      http.get<{ itemId: number; status: string }[]>('/report-request-items', { requestId }),
      http.get<any[]>('/report-comments', { requestId }),
      http.get<MailLog[]>('/mail-logs', { requestId }),
    ]);
    return {
      items: Object.fromEntries(ri.map((x) => [x.itemId, ITEM_STATUS.toUi(x.status)!])),
      comments: cs.map((c) => ({ ...c, side: SIDE.toUi(c.side)! })).sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
      mails: ms.sort((a, b) => b.sentAt.localeCompare(a.sentAt)),
    };
  },
  create: (f: { year: number; term: string; startOn: string; dueOn: string | null; includeRisk: boolean; companyIds: number[]; itemIds: number[] }) =>
    http.post<{ id: number }>('/report-rounds/create', { ...f, term: TERM.toDb(f.term), actorName: ACTOR }),
  close: (roundId: number) => http.post(`/report-rounds/${roundId}/close`, { actorName: ACTOR }),
  send: (id: number) => http.post<any>(`/report-requests/${id}/send`, { actorName: ACTOR }).then(toReq),
  submit: (id: number) => http.post<any>(`/report-requests/${id}/submit`, { actorName: ACTOR }).then(toReq),
  confirm: (id: number) => http.post<any>(`/report-requests/${id}/confirm`, { actorName: ACTOR }).then(toReq),
  reject: (id: number, reason: string, replyDueOn: string) =>
    http.post<any>(`/report-requests/${id}/reject`, { reason, replyDueOn, actorName: ACTOR }).then(toReq),
  setItem: (requestId: number, itemId: number, status: ItemStatus) =>
    http.put('/report-request-items', { requestId, itemId, status: ITEM_STATUS.toDb(status) }),
  /** 투자처 쪽 글은 받은 메일을 옮겨 적은 것 — 글쓴이는 회사 이름 */
  addComment: (requestId: number, body: string, side: '우리' | '투자처', authorName: string) =>
    http.post('/report-comments', { requestId, body, side: SIDE.toDb(side), authorName }),
};
